import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { BillingApi } from '@eis/billingservice-sdk';
import {
  addMonths,
  formatCivilDate,
  formatCurrency,
  formatPeriod,
  isBillingUnavailable,
  links,
  resolveFormattingLocale,
  useEstimate,
  usePolicyBilling,
  usePolicyInvoices,
} from '@eis/core';
import { DetailsSection, EmptyState, ErrorState, StatusChip } from '@eis/patterns';

/**
 * The Gruppen-Police's invoices (SDD-015 §7.2, SSOT I-20): the REAL master invoices the billing
 * service issued — one document per period at master level, itemised per member — read through
 * `@eis/core`, plus the master's schedule row (B-10) and its estimate when one exists. Since prompt
 * 21 phase 6 this replaces the insurance service's `ListGroupPolicyInvoices` projection (R-2), which
 * priced a period on the fly and never was a document.
 *
 * The lines ARE the "delegated invoices": informational, never separately dunned. Rendering them as
 * a nested table rather than as separate documents is the model showing through — there is exactly
 * one billed document, at master level.
 */
const civil = (value: string | undefined): string => (value ?? '').slice(0, 10);

function periodOf(from: string | undefined, to: string | undefined, locale: string): string {
  const start = civil(from);
  const end = civil(to);
  if (start === '') return '—';
  if (start.endsWith('-01') && addMonths(start, 1) === end) return formatPeriod(start.slice(0, 7), locale);
  return `${formatCivilDate(start, locale)} – ${end === '' ? '' : formatCivilDate(end, locale)}`;
}

const money = (minor: number | undefined, currency: string | undefined, locale: string): string =>
  minor === undefined ? '—' : formatCurrency(minor, currency === undefined || currency === '' ? 'EUR' : currency, locale);

function InvoiceRow({ invoice, locale }: { readonly invoice: BillingApi.Invoice; readonly locale: string }): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const lines = invoice.invoiceItems ?? [];
  const currency = invoice.currency;

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={open ? t('groupPolicies.invoices.collapse') : t('groupPolicies.invoices.expand')}
            onClick={() => {
              setOpen((current) => !current);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{periodOf(invoice.billingIntervalFrom, invoice.billingIntervalTo, locale)}</TableCell>
        <TableCell>
          <Link
            component="button"
            type="button"
            sx={{ fontFamily: 'monospace' }}
            onClick={() => {
              void navigate({ href: links.billing.invoice(invoice.code ?? '') });
            }}
          >
            {invoice.invoiceNumber === undefined || invoice.invoiceNumber === '' ? invoice.code : invoice.invoiceNumber}
          </Link>
        </TableCell>
        <TableCell>
          <Chip size="small" label={t(`policies.billing.types.${invoice.type ?? ''}` as never, { defaultValue: invoice.type ?? '' })} />
        </TableCell>
        <TableCell align="right">{money(invoice.grossAmount, currency, locale)}</TableCell>
        <TableCell>
          <StatusChip
            status={invoice.status ?? ''}
            label={t(`policies.billing.invoiceStatus.${invoice.status ?? ''}` as never, { defaultValue: invoice.status ?? '' })}
          />
        </TableCell>
        <TableCell align="right">{lines.length}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 2 }}>
              {/* h3, not the h6 `subtitle2` renders by default: the section above is an h2, and
                  a heading level that skips one is an axe violation and a screen-reader lie. */}
              <Typography variant="subtitle2" component="h3" gutterBottom>
                {t('groupPolicies.invoices.lines.title')}
              </Typography>
              <Table size="small" aria-label={t('groupPolicies.invoices.lines.title')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('groupPolicies.invoices.lines.policy')}</TableCell>
                    <TableCell>{t('groupPolicies.invoices.lines.period')}</TableCell>
                    <TableCell align="right">{t('groupPolicies.invoices.lines.amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={`${line.policyCode ?? ''}-${String(index)}`}>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          sx={{ fontFamily: 'monospace' }}
                          onClick={() => {
                            void navigate({ href: links.insurance.policy(line.policyCode ?? '') });
                          }}
                        >
                          {line.policyCode ?? '—'}
                        </Link>
                      </TableCell>
                      <TableCell>{periodOf(line.billingIntervalFrom, line.billingIntervalTo, locale)}</TableCell>
                      <TableCell align="right">{money(line.grossAmount, currency, locale)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function GroupPolicyInvoicesTab({ code }: { readonly code: string }): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const invoices = usePolicyInvoices(code);
  const schedule = usePolicyBilling(code);
  const estimate = useEstimate(code);

  const failed = [invoices, schedule, estimate].find((query) => query.isError);
  if (failed?.isError === true) {
    return (
      <ErrorState
        description={isBillingUnavailable(failed.error) ? t('policies.billing.unavailable') : t('policies.billing.error')}
        details={failed.error.message}
        onRetry={() => {
          void invoices.refetch();
          void schedule.refetch();
          void estimate.refetch();
        }}
      />
    );
  }

  const items = invoices.data?.items ?? [];
  const row = schedule.data?.policyBilling;
  const nextDate = civil(row?.nextBillingDate);
  const running = estimate.data;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h3" component="h2">
          {t('groupPolicies.invoices.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('groupPolicies.invoices.description')}
        </Typography>
      </Box>

      <Alert severity="info">{t('groupPolicies.invoices.dunningNotice')}</Alert>

      {schedule.isPending ? (
        <Skeleton variant="rounded" height={96} />
      ) : (
        <DetailsSection
          title={t('groupPolicies.invoices.schedule.title')}
          fields={[
            {
              label: t('groupPolicies.invoices.schedule.status'),
              value:
                row === undefined ? (
                  t('policies.billing.notScheduled')
                ) : (
                  <StatusChip
                    status={row.status ?? ''}
                    label={t(`policies.billing.scheduleStatus.${row.status ?? ''}` as never, { defaultValue: row.status ?? '' })}
                  />
                ),
            },
            { label: t('groupPolicies.invoices.schedule.nextDate'), value: nextDate === '' ? '—' : formatCivilDate(nextDate, locale) },
            { label: t('groupPolicies.invoices.schedule.nextPeriod'), value: periodOf(row?.nextPeriodFrom, row?.nextPeriodTo, locale) },
            {
              label: t('groupPolicies.invoices.schedule.estimate'),
              value:
                running === null || running === undefined ? (
                  t('policies.billing.noEstimate')
                ) : (
                  // ONE estimate for the Gruppen-Police with a line per member (A-52); the
                  // breakdown is the document itself.
                  <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <span data-testid="running-estimate">
                      {`${money(running.grossAmount, running.currency, locale)} · ${periodOf(running.billingIntervalFrom, running.billingIntervalTo, locale)}`}
                    </span>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        void navigate({ href: links.billing.invoice(running.code ?? '') });
                      }}
                    >
                      {t('groupPolicies.invoices.schedule.estimateBreakdown', { count: running.invoiceItems?.length ?? 0 })}
                    </Link>
                  </Stack>
                ),
            },
          ]}
        />
      )}

      {invoices.isPending ? (
        <Skeleton variant="rounded" height={220} />
      ) : items.length === 0 ? (
        <EmptyState description={t('groupPolicies.invoices.empty')} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label={t('groupPolicies.invoices.title')}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Box component="span" sx={{ position: 'absolute', left: -9999 }}>
                    {t('groupPolicies.invoices.expand')}
                  </Box>
                </TableCell>
                <TableCell>{t('groupPolicies.invoices.columns.period')}</TableCell>
                <TableCell>{t('groupPolicies.invoices.columns.id')}</TableCell>
                <TableCell>{t('groupPolicies.invoices.columns.type')}</TableCell>
                <TableCell align="right">{t('groupPolicies.invoices.columns.total')}</TableCell>
                <TableCell>{t('groupPolicies.invoices.columns.status')}</TableCell>
                <TableCell align="right">{t('groupPolicies.invoices.columns.lines')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((invoice) => (
                <InvoiceRow key={invoice.code ?? ''} invoice={invoice} locale={locale} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
